/**
 * Responsibility: the grading arbitrage page - which raw cards are worth
 * buying and sending to PSA.
 *
 * A separate system from restock monitoring and it reads like one. Nothing
 * here is time sensitive, nothing alerts, and nothing polls: the page is a
 * shortlist to read deliberately, refreshed when the operator asks.
 *
 * Two things are stated before any number, because they decide how much every
 * number below is worth. Whether sold prices are available at all, and the
 * cost assumptions the payoffs were computed under. A page that showed a
 * ranked table without saying "these are asking prices and grading is assumed
 * to cost $50" would be presenting guesses as findings.
 *
 * The mobile rule from WatchlistPage applies here too: the stat row must not
 * be able to widen the page. A flex item defaults to min-width:auto, so the
 * table's intrinsic width would otherwise propagate up through the column and
 * slide the whole page sideways on a phone.
 */
import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { PageContainer, ScrollArea, SectionCard } from '@kit/components';
import { FromWatchedDialog } from '../components/grading/FromWatchedDialog';
import { GradingStats } from '../components/grading/GradingStats';
import { useDiscoveryArchive } from '../api/hooks/useDiscovery';
import { useSaved } from '../api/hooks/useMarket';
import { AddCandidateDialog } from '../components/grading/AddCandidateDialog';
import { OpportunityDetail } from '../components/grading/OpportunityDetail';
import { FittedTable } from '../components/shelf/FittedTable';
import { OpportunityTable } from '../components/grading/OpportunityTable';
import { PopulationDialog } from '../components/grading/PopulationDialog';
import { RejectedList } from '../components/grading/RejectedList';
import { money, percent } from '../components/grading/money';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import {
  useAddCandidate,
  useDeleteCandidate,
  useAddFromLike,
  useAddFromWatched,
  useGradingBoard,
  useRefreshCandidate,
  useRefreshGrading,
  usePatchCandidate,
  useSetPopulation,
} from '../api/hooks/useGrading';
import type { Opportunity } from '../api/client';

/** Enough to name the card in a confirmation without holding the whole row. */
type Removable = { id: string; card_name: string };

export function GradingPage() {
  const theme = useTheme();
  // Below md the window scrolls as one document, which is right on a phone:
  // a nested scroller inside a short viewport is two things to drag.
  const onDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const { data: board, error, isLoading } = useGradingBoard();
  const add = useAddCandidate();
  const refreshAll = useRefreshGrading();
  const refreshOne = useRefreshCandidate();
  const patch = usePatchCandidate();
  const setPopulation = useSetPopulation();
  const remove = useDeleteCandidate();

  const [adding, setAdding] = useState(false);
  // Pulling in something already being watched, from either list.
  const [importing, setImporting] = useState(false);
  const [detail, setDetail] = useState<Opportunity | null>(null);
  const [editingPopulation, setEditingPopulation] = useState<Opportunity | null>(null);
  const [removing, setRemoving] = useState<Removable | null>(null);

  // Both watching bins, and the likes. Only fetched while the page is
  // open; the dialog reads whatever these already hold.
  const watchedModern = useDiscoveryArchive('MODERN', 'WATCHING');
  const watchedVintage = useDiscoveryArchive('VINTAGE', 'WATCHING');
  const saved = useSaved();
  const fromWatched = useAddFromWatched();
  const fromLike = useAddFromLike();

  const opportunities = board?.opportunities ?? [];
  const rejected = board?.rejected ?? [];
  const assumptions = board?.assumptions;
  const busy =
    refreshAll.isPending || refreshOne.isPending || patch.isPending || remove.isPending;

  const flagged = opportunities.filter((row) => row.watched);
  const sound = opportunities.filter((row) => row.is_sold_backed && !row.is_falling_sharply);

  const watching = [
    ...(watchedModern.data?.rows ?? []),
    ...(watchedVintage.data?.rows ?? []),
  ];
  const likes = saved.data?.likes ?? [];
  // What is already on the list, so a row can say so instead of being
  // silently addable twice.
  const already = new Set(
    [...opportunities, ...rejected].map((row) => row.card_name.trim().toLowerCase()),
  );

  return (
    <PageContainer disableBodyScroll={onDesktop}>
      <Stack spacing={2} sx={{ minWidth: 0, height: '100%' }}>
        {error ? <Alert severity="error">{(error as Error).message}</Alert> : null}
        {fromWatched.error || fromLike.error ? (
          <Alert severity="error">
            {((fromWatched.error ?? fromLike.error) as Error).message}
          </Alert>
        ) : null}

        {board && !board.sources.sold_prices_available ? (
          <Alert severity="warning">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Every price here comes from live listings, not completed sales.
            </Typography>
            <Typography variant="body2">
              {board.sources.note} Until then, treat these payoffs as optimistic: sellers ask well
              above what copies actually sell for, and the gap on graded singles runs to a third or
              more.
            </Typography>
          </Alert>
        ) : null}

        {board && !board.sources.population_available ? (
          <Alert severity="info">
            {board.sources.population_note} Without a gem rate only the best case can be
            computed, and the best case flatters every card - so each row offers a dialog to
            enter the counts by hand.
          </Alert>
        ) : null}

        <GradingStats
          opportunities={opportunities}
          sound={sound.length}
          flagged={flagged.length}
          rejected={rejected.length}
        />

        {/* Under the figures, like the controls on every other page. Above
            them it read as a header band, and put three buttons between the
            person and the numbers they came to see. */}
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          justifyContent="flex-end"
          sx={{ flexShrink: 0 }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refreshAll.mutate(undefined)}
            disabled={busy || opportunities.length + rejected.length === 0}
          >
            {refreshAll.isPending ? 'Re-pricing' : 'Re-price all'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => setImporting(true)}
          >
            From watching
            {watching.length + likes.length > 0 ? ` (${watching.length + likes.length})` : ''}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAdding(true)}
          >
            Consider a card
          </Button>
        </Stack>

        {assumptions ? (
          <Typography variant="caption" color="text.secondary">
            Computed on buying {assumptions.cards_bought} raw copies, {' '}
            {money(assumptions.grading_cost_per_card)} per card to grade, {' '}
            {money(assumptions.submission_shipping)} to ship the submission both ways, and {' '}
            {percent(assumptions.sell_fee_rate)} of the sale in marketplace fees.
            {assumptions.sell_the_nines
              ? ' The copies that come back 9 are assumed sold too.'
              : ' The copies that come back 9 are assumed unsellable.'}
          </Typography>
        ) : null}

        <ScrollArea>
          <Stack spacing={3} sx={{ pb: 2 }}>
        <SectionCard
          title="Shortlist"
          subtitle="Sound rows first, then by payoff. A falling price or a price with no sales behind it drops a card below everything trustworthy, however large its arithmetic."
        >
          <FittedTable>
            <OpportunityTable
              rows={opportunities}
              loading={isLoading}
              busy={busy}
              onOpen={setDetail}
              onRefresh={(row) => refreshOne.mutate(row.id)}
              onToggleWatched={(row) =>
                patch.mutate({ id: row.id, patch: { watched: !row.watched } })
              }
              onRemove={(row) => setRemoving({ id: row.id, card_name: row.card_name })}
            />
          </FittedTable>
        </SectionCard>

        <SectionCard
          title="Passed over"
          subtitle="On the list, but not worth grading at today's prices."
        >
          <RejectedList
            rows={rejected}
            busy={busy}
            onRefresh={(row) => refreshOne.mutate(row.id)}
            onRemove={(row) => setRemoving({ id: row.id, card_name: row.card_name })}
          />
        </SectionCard>
          </Stack>
        </ScrollArea>
      </Stack>

      <AddCandidateDialog
        open={adding}
        pending={add.isPending}
        error={add.error ? (add.error as Error).message : null}
        soldPricesAvailable={board?.sources.sold_prices_available ?? false}
        populationAvailable={board?.sources.population_available ?? false}
        onClose={() => {
          setAdding(false);
          add.reset();
        }}
        onSubmit={(payload) =>
          add.mutate(payload, {
            onSuccess: () => setAdding(false),
          })
        }
      />

      <FromWatchedDialog
        open={importing}
        watched={watching}
        likes={likes}
        already={already}
        busy={fromWatched.isPending || fromLike.isPending}
        onClose={() => setImporting(false)}
        onAddWatched={(discoveryKey) => fromWatched.mutate(discoveryKey)}
        onAddLike={(productKey) => fromLike.mutate(productKey)}
      />

      <OpportunityDetail
        row={detail}
        onClose={() => setDetail(null)}
        onEditPopulation={(row) => {
          setDetail(null);
          setEditingPopulation(row);
        }}
      />

      <PopulationDialog
        row={editingPopulation}
        pending={setPopulation.isPending || patch.isPending}
        populationAvailable={board?.sources.population_available ?? false}
        onClose={() => setEditingPopulation(null)}
        onSubmitCounts={(id, population) =>
          setPopulation.mutate(
            { id, population },
            { onSuccess: () => setEditingPopulation(null) },
          )
        }
        onSubmitSpecId={(id, psa_spec_id) =>
          patch.mutate(
            { id, patch: { psa_spec_id } },
            { onSuccess: () => setEditingPopulation(null) },
          )
        }
      />

      <ConfirmDialog
        open={removing !== null}
        title="Remove this card"
        body={`${removing?.card_name ?? 'This card'} and its recorded sales history will be deleted. Adding it again starts from nothing.`}
        confirmLabel="Remove"
        pending={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          if (!removing) return;
          remove.mutate(removing.id, { onSuccess: () => setRemoving(null) });
        }}
      />
    </PageContainer>
  );
}
